import type { WishlistActionType } from './WishlistActionType.enum';
import type { ApolloError } from '@apollo/client';
import type { ReactElement, MouseEvent } from 'react';
import type { RuleSet } from 'styled-components';

import { useRouter } from 'next/compat/router';
import dynamic from 'next/dynamic';
import { useState, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { CartErrorTooltip } from './CartErrorTooltip';
import type { ProductAddActions as ProductAddActionsSchema } from '~/amplienceTypes/schemas/imported/product-add-actions-schema';
import type { CTAVariantProps } from '~/components/shared/core/button/CTA';
import { CTA } from '~/components/shared/core/button/CTA';
import { media } from '~/components/shared/core/styles';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import type { StyliticsPlacement } from '~/components/shared/recommendations/stylitics/types';
import { useStyliticsInsights } from '~/components/shared/recommendations/stylitics/useStyliticsInsights';
import { useAlgoliaInsights } from '~/components/shared/vendors/useAlgoliaInsights';
import { ADD_TO_CART_BUTTON_ID } from '~/utilities/constants/elementIds';
import { FULLSTORY_EVENTS } from '~/utilities/constants/fullstoryEvents';
import {
  ALGOLIA_OBJECT_ID,
  CART_ENTRIES,
  CART_MUTATED,
  CART_STORAGE_VALUE,
  HYBRIS_CART_STORAGE_VALUE,
  PLP_NAVIGATED_PRODUCT,
} from '~/utilities/constants/localStorageKeys';
import { useCartContext } from '~/utilities/context/dynamic/CartContext';
import { usePDPDynamicContext } from '~/utilities/context/dynamic/PDPDynamicContext';
import type { GridValues } from '~/utilities/context/dynamic/SizeContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Product } from '~/utilities/graphql/codegen';
import {
  SiteType,
  SizeType,
  StockLevelMessageKey,
  useAddToCartMutation,
} from '~/utilities/graphql/codegen';
import { storageAvailable } from '~/utilities/helpers';
import { getProductBaseCode } from '~/utilities/helpers/getProductBaseCode';
import { trackFullstoryEvent } from '~/utilities/helpers/syncGlobalObject';
import { useOuterClick } from '~/utilities/hooks';
import { parseCartUpdateDataLayer, getProductDataLayerEvent } from '~/utilities/vendors';
import type { ProductClickViewType } from '~/utilities/vendors/dataLayer.type';
import { storeFitAnalyticsRecommendation } from '~/utilities/vendors/fitAnalytics';

const WishlistAction = dynamic(async () => (await import('./WishlistAction')).WishlistAction);

const GStarAddToBagStyle = css`
  font-size: ${({ theme }) => theme.font.size[3]};
  font-weight: ${({ theme }) => theme.variables.font.weight.bold};
`;

const AafAddToBagStyle = css`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;

const WahtsAddToBagStyle = css`
  font-size: ${({ theme }) => theme.font.size[3]};
`;

const GStarProductActionsStyle = css`
  margin-bottom: ${({ theme }) => theme.spacing.y.md};
`;

const AafProductActionsStyle = css`
  margin: ${({ theme }) => `50px 0 ${theme.spacing.y.lg}`};

  @media ${({ theme }) => media(theme).lessThan('sm')} {
    margin: ${({ theme }) => `32px 0 ${theme.spacing.y.lg}`};
  }
`;

const AddToBagConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: GStarAddToBagStyle,
  [SiteType.Wahts]: WahtsAddToBagStyle,
  [SiteType.Outlet]: GStarAddToBagStyle,
  [SiteType.Employeeshop]: GStarAddToBagStyle,
  [SiteType.Aaf]: AafAddToBagStyle,
};

const ProductActionsConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: GStarProductActionsStyle,
  [SiteType.Wahts]: GStarProductActionsStyle,
  [SiteType.Outlet]: GStarProductActionsStyle,
  [SiteType.Employeeshop]: GStarProductActionsStyle,
  [SiteType.Aaf]: AafProductActionsStyle,
};

const S = {
  ProductActions: styled.div`
    display: flex;
    position: relative;
    gap: 8px;
    ${({ theme }) => ProductActionsConfig[theme.siteType]};
  `,

  AddToBag: styled(CTA)`
    width: 100%;

    ${({ theme }) => AddToBagConfig[theme.siteType]}
  `,
};

const CTAVariantPropsMap: Record<SiteType, CTAVariantProps> = {
  [SiteType.Gstar]: { variant: 'legacy', size: 'md' },
  [SiteType.Outlet]: { variant: 'legacy', size: 'md' },
  [SiteType.Employeeshop]: { variant: 'legacy', size: 'md' },
  [SiteType.Aaf]: { variant: 'legacy', size: 'lg' },
  [SiteType.Wahts]: { variant: 'primary', size: 'md' },
};

export interface ProductAddActionsProps {
  setDlgOpen?: (val: boolean) => void;
  sizeCode: string;
  gridValues: GridValues;
  product: Product;
  icon?: 'shoppingBag';
  sizeErrorTitle?: string;
  onSizeError?: (val: boolean) => void;
  wishlistButtonType: WishlistActionType;
  viewType?: ProductClickViewType;
  onAdded?: () => void;
  algoliaAddToCartEventName: string;
  algoliaAddToCartAfterSearchEventName: string;
  trackFitAnalyticsSizes?: boolean;
  placement?: StyliticsPlacement;
}

export const ProductAddActions = ({
  notifyBtnV2,
  addToCartTxtV2,
  addingToCartTxtV2,
  sizeErrorTxtV2,
  setDlgOpen,
  sizeCode,
  gridValues,
  product,
  icon,
  sizeErrorTitle,
  onSizeError,
  wishlistButtonType,
  viewType = 'general view',
  onAdded,
  algoliaAddToCartEventName = '',
  algoliaAddToCartAfterSearchEventName = '',
  trackFitAnalyticsSizes = false,
  placement = 'main',
}: ProductAddActionsSchema & ProductAddActionsProps): ReactElement => {
  const {
    dynamicProductData: { comingSoon: dynamicComingSoon, stockInformation },
  } = usePDPDynamicContext();
  const [adding, setAdding] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [cartMutationError, setCartMutationError] = useState('');

  const { ref, outerClicked, setOuterClicked } = useOuterClick();
  const { locale, country, siteType } = useAppContext();
  const { pushToDataLayer } = useDataLayerContext();
  const { setMiniCart, showAddToBagNotification } = useCartContext();
  const { pushToAlgolia, getAlgoliaDataCart } = useAlgoliaInsights();
  const {
    configuration: { enableExponea },
  } = useStaticContext();

  const productCode = useMemo(() => getProductBaseCode(product), [product]);
  const styliticInsights = useStyliticsInsights(productCode);

  const isAafSite = siteType === SiteType.Aaf;

  const [addToCartMutation] = useAddToCartMutation();

  const router = useRouter();

  const comingSoon = dynamicComingSoon ?? product.comingSoon;
  const stockInfo = stockInformation ?? product.stockInformation;
  const isComingSoonOrOutOfStock =
    comingSoon || (stockInfo?.stockLevel === StockLevelMessageKey.None && product.oneSize);

  if (stockInfo?.stockLevel === StockLevelMessageKey.None && !comingSoon && !product.oneSize) {
    return <></>;
  }

  // sonarlint rule failing: cyclomatic complexity
  const addToCart = /* NOSONAR */ async () => {
    try {
      setOuterClicked(false);
      setCartMutationError('');

      const selectedSizeCode = sizeCode;

      setSizeError(false);
      onSizeError?.(false);
      setAdding(true);

      if (trackFitAnalyticsSizes) {
        const isWidgetRecommendation = false;

        storeFitAnalyticsRecommendation(selectedSizeCode, isWidgetRecommendation);
      }

      const listerClickedProductCode = sessionStorage?.getItem(PLP_NAVIGATED_PRODUCT);
      const algoliaData = {
        currency: product.price?.currencyIso,
        objectData: [
          {
            price: product.price?.value,
            quantity: 1,
            ...(product?.discount && { discount: product.discount }),
          },
        ],
      };
      let algoliaDataToCart;
      let pushAlgoliaEvents;

      if (product?.baseProduct && !listerClickedProductCode?.includes(product.baseProduct)) {
        // Scenario 1: send algolia event when user landed directly to PDP
        pushAlgoliaEvents = () => {
          pushToAlgolia(
            'addedToCartObjectIDs',
            algoliaAddToCartEventName,
            product.algoliaObjectId,
            false,
            algoliaData
          );
        };

        algoliaDataToCart = getAlgoliaDataCart({
          purchased: { objectID: product.algoliaObjectId },
        });
      } else if (listerClickedProductCode === selectedSizeCode) {
        // Scenario 2: send algolia event when user adds to card the same size variant the user saw on lister page
        pushAlgoliaEvents = () => {
          pushToAlgolia(
            'addedToCartObjectIDsAfterSearch',
            algoliaAddToCartAfterSearchEventName,
            product.algoliaObjectId,
            true,
            algoliaData
          );
        };

        algoliaDataToCart = getAlgoliaDataCart({
          purchased: { objectID: product.algoliaObjectId, queryID: '' },
        });
      } else {
        // Scenario 3: send algolia event when user adds to card a size variant different from the one the user saw on lister page
        const listerClickedObjectId = sessionStorage?.getItem(ALGOLIA_OBJECT_ID);

        pushAlgoliaEvents = () => {
          pushToAlgolia(
            'addedToCartObjectIDs',
            algoliaAddToCartEventName,
            product.algoliaObjectId,
            false,
            algoliaData
          );

          if (listerClickedObjectId) {
            pushToAlgolia(
              'addedToCartObjectIDsAfterSearch',
              algoliaAddToCartAfterSearchEventName,
              listerClickedObjectId,
              true,
              algoliaData
            );
          }
        };

        algoliaDataToCart = getAlgoliaDataCart({
          purchased: {
            objectID: product.algoliaObjectId,
          },
          ...(listerClickedObjectId && {
            viewed: {
              objectID: listerClickedObjectId,
              queryID: '',
            },
          }),
        });
      }

      const response = await addToCartMutation({
        variables: {
          siteType,
          code: selectedSizeCode,
          locale,
          ...(algoliaDataToCart && { eventPayload: JSON.stringify(algoliaDataToCart) }),
        },
      });

      const cartData = response.data;

      let quantity = 0;

      if (
        cartData?.addToCart?.cartModifications &&
        cartData?.addToCart?.cartModifications[0]?.statusCode !== 'success'
      ) {
        setCartMutationError(cartData?.addToCart?.cartModifications[0]?.statusMessage || '');
      } else if (cartData?.addToCart?.cart) {
        quantity = 1;

        if (storageAvailable('localStorage')) {
          localStorage.setItem(`${country}${CART_MUTATED}`, CART_STORAGE_VALUE);
          localStorage.setItem(
            `${CART_ENTRIES}_${locale}`,
            JSON.stringify(cartData?.addToCart?.cart)
          );
        }

        pushAlgoliaEvents?.();

        setMiniCart(cartData?.addToCart?.cart);

        const cartProduct = cartData?.addToCart?.cart?.entries?.find(
          entry => entry?.product?.code?.toLowerCase() === selectedSizeCode.toLowerCase()
        )?.product;
        const dataLayerProduct = cartProduct || {
          ...product,
          baseProduct: product.baseProduct || product.code,
          code: selectedSizeCode,
          size: { value: (gridValues.gridValue1 || '') + gridValues.gridValue2 },
          ean: product.sizeInformation?.sizes?.find(size =>
            product.sizeInformation?.sizeType === SizeType.NumericDouble
              ? size?.gridValue1?.toLowerCase() === gridValues.gridValue1?.toLowerCase() &&
                size?.gridValue2?.toLowerCase() === gridValues.gridValue2?.toLowerCase()
              : size?.gridValue1?.toLowerCase() === gridValues.gridValue1?.toLowerCase()
          )?.ean,
        };

        showAddToBagNotification({ product: cartProduct });

        trackFullstoryEvent(FULLSTORY_EVENTS.addToCart, { product_code: productCode });

        if (enableExponea) {
          const exponeaCartUpdateProperties = parseCartUpdateDataLayer('add', cartData?.addToCart);

          pushToDataLayer({
            event: 'exponea_cart_update',
            event_name: 'cart_update',
            namespace: 'exponea',
            event_properties: exponeaCartUpdateProperties,
          });
        }

        const eecAddToCartEvent = getProductDataLayerEvent('eecAddToCart', {
          product: dataLayerProduct,
          quantity,
          viewType,
        });

        if (eecAddToCartEvent) {
          pushToDataLayer(eecAddToCartEvent);
        }
      }

      if (quantity > 0) {
        onAdded?.();
        styliticInsights?.trackAddItemToCart({
          itemId: productCode,
          placement,
          price: product.price?.value,
          quantity,
        });
      }
    } catch (e) {
      const message = (e as ApolloError)?.message;

      if (message?.includes('440') || message?.includes('session not available')) {
        if (storageAvailable('localStorage')) {
          localStorage.setItem(`${country}${CART_MUTATED}`, HYBRIS_CART_STORAGE_VALUE);
        }

        router?.reload();
      } else {
        // Is the error message from hybris localized?
        // If so, i'll replace the message below with the error response from hybris, otherwise, need a new field in product-add-actions-schema for error text
        const generic500ErrorMsg = 'Product add action error';

        setCartMutationError(generic500ErrorMsg);
      }
    } finally {
      setAdding(false);
    }
  };

  const buttonLabel = () => {
    if (adding) {
      return addingToCartTxtV2 || '';
    }

    if (isComingSoonOrOutOfStock) {
      return notifyBtnV2 || '';
    }

    return addToCartTxtV2 || '';
  };

  return (
    <S.ProductActions>
      <S.AddToBag
        id={ADD_TO_CART_BUTTON_ID}
        label={buttonLabel()}
        icon={isAafSite || comingSoon ? undefined : icon}
        disabled={adding}
        onClick={(e: MouseEvent) => {
          (e?.currentTarget as HTMLButtonElement)?.blur();

          if (isComingSoonOrOutOfStock) {
            setDlgOpen?.(true);
          } else if (!sizeCode) {
            setOuterClicked(false);
            setSizeError(true);
            onSizeError?.(true);
          } else {
            addToCart();
          }
        }}
        data-testid={isComingSoonOrOutOfStock ? 'notify-when-in-stock' : 'add-to-cart'}
        {...CTAVariantPropsMap[siteType]}
      />
      {!isComingSoonOrOutOfStock && (
        <FeatureFlag flag="enableWishlist">
          <WishlistAction
            productCode={product.baseProduct || product.code}
            wishlistButtonType={wishlistButtonType}
          />
        </FeatureFlag>
      )}
      {(sizeError || cartMutationError) && (
        <CartErrorTooltip
          cartMutationError={cartMutationError}
          outerClicked={outerClicked}
          sizeError={sizeError}
          sizeErrorText={sizeErrorTitle || sizeErrorTxtV2}
          tooltipRef={ref}
          viewType={viewType}
        />
      )}
    </S.ProductActions>
  );
};
